import React, { useContext, useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DischargedContext } from "../../../context/DischargedContext";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Chip,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTranslation } from "react-i18next";
import useClinicalLocations from "../../../hooks/useClinicalLocations";
import { useObservationsModal } from "../../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../../components/Advanced/DataGrid/observationsColumn";
import { formatDateForTable } from "../../../common/helpers";

// Valores permitidos por el SP (ver CENSUS_ENDPOINTS.md §2).
const FOLLOW_UP_STATUS_OPTIONS = [
  { id: 42, name: "Pending" },
  { id: 45, name: "Cancelled" },
  { id: 46, name: "Cancelled (equivalent)" },
];

const DischargedTable = ({ selectedLocation, setSelectedLocation }) => {
  const { t } = useTranslation();
  const { showObservationsModal } = useObservationsModal();
  const { locations: clinicalLocations } = useClinicalLocations();
  const { dischargedPatients, loading, error, filters, updateFilter, resetFilters } =
    useContext(DischargedContext);
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  // Inputs de texto con debounce para no re-fetch en cada tecla.
  const [searchNameDraft, setSearchNameDraft] = useState("");
  const [cipIdDraft, setCipIdDraft] = useState("");

  useEffect(() => {
    const id = setTimeout(() => {
      updateFilter("searchName", searchNameDraft.trim() || null);
    }, 400);
    return () => clearTimeout(id);
  }, [searchNameDraft, updateFilter]);

  useEffect(() => {
    const id = setTimeout(() => {
      updateFilter("cipId", cipIdDraft.trim() || null);
    }, 400);
    return () => clearTimeout(id);
  }, [cipIdDraft, updateFilter]);

  useEffect(() => {
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [selectedLocation]);

  const handleLocationChange = (value) => {
    setSelectedLocation(value);
    updateFilter("locationId", value === "" ? null : value);
  };

  const handleReset = () => {
    setSearchNameDraft("");
    setCipIdDraft("");
    setSelectedLocation("");
    resetFilters();
  };

  const handleViewPatient = (row) => {
    navigate(`/patient-info/${row.cipId}`, {
      state: {
        patientRow: {
          ...row,
          // Alias retrocompatibles para PatientInfoPage (lee memberFullName/memberId/dob como fallback).
          memberFullName: row.fullName,
          memberId: row.cipId,
          dob: row.dateOfBirth,
        },
        returnPath: location.pathname,
      },
    });
  };

  if (loading) return <Typography>{t("Main.Loading")}</Typography>;
  if (error) return <Typography color="error">Error loading data.</Typography>;

  const columns = [
    getObservationsColumn(showObservationsModal, 5, {
      getCipId: (row) => row.cipId,
      getPatientName: (row) => row.fullName || "",
    }),
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View Patient Info">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPatient(params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "isOverdue",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const overdue = params.row.isOverdue;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon sx={{ color: overdue ? "#E74C3C" : "#00A650" }} />
            <span style={{ color: overdue ? "#A10000" : "#007A33" }}>
              {overdue ? "Overdue" : params.row.followUpStatusName || "—"}
            </span>
          </Box>
        );
      },
    },
    { field: "cipId", headerName: "CIP ID", width: 140 },
    { field: "fullName", headerName: "Patient Name", flex: 2, minWidth: 260 },
    {
      field: "dateOfBirth",
      headerName: "DOB",
      width: 120,
      valueGetter: (value) => formatDateForTable(value),
    },
    { field: "gender", headerName: "Gender", width: 80 },
    { field: "phoneNumber", headerName: "Phone", width: 150 },
    { field: "mobileNumber", headerName: "Mobile", width: 150 },
    {
      field: "dischargeDate",
      headerName: "Discharge Date",
      width: 140,
      valueGetter: (value) => formatDateForTable(value),
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 120,
      valueGetter: (value) => formatDateForTable(value),
    },
    {
      field: "laceScore",
      headerName: "LACE+",
      width: 90,
      renderCell: (params) =>
        params.value != null ? (
          <Chip
            size="small"
            label={params.value}
            color={params.value >= 15 ? "error" : params.value >= 10 ? "warning" : "default"}
          />
        ) : (
          "—"
        ),
    },
    { field: "facilityName", headerName: "Facility", flex: 1.5, minWidth: 220 },
    {
      field: "diagnoses",
      headerName: "Diagnoses (ICD)",
      flex: 1.5,
      minWidth: 220,
      valueGetter: (value) => value || "—",
    },
    { field: "attendingPhysicianName", headerName: "Attending Physician", flex: 1, minWidth: 180 },
    { field: "followUpStatusName", headerName: "Follow-up", width: 130 },
    {
      field: "callCount",
      headerName: "Calls",
      width: 80,
      renderCell: (params) =>
        params.value > 0 ? (
          <Chip size="small" label={params.value} color="primary" />
        ) : (
          <span>0</span>
        ),
    },
    {
      field: "lastContactDate",
      headerName: "Last Contact",
      width: 130,
      valueGetter: (value) => formatDateForTable(value),
    },
    { field: "medicalCenter", headerName: "Medical Center", flex: 1.2, minWidth: 180 },
  ];

  return (
    <>
      {/* Filtros */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          px: 1,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          label="Search by name"
          value={searchNameDraft}
          onChange={(e) => setSearchNameDraft(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          label="CIP ID"
          value={cipIdDraft}
          onChange={(e) => setCipIdDraft(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="DOB"
          InputLabelProps={{ shrink: true }}
          value={filters.dateOfBirth || ""}
          onChange={(e) => updateFilter("dateOfBirth", e.target.value || null)}
          sx={{ minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="discharged-location-label">Location</InputLabel>
          <Select
            labelId="discharged-location-label"
            value={selectedLocation}
            label="Location"
            onChange={(e) => handleLocationChange(e.target.value)}
          >
            <MenuItem value="">All Locations</MenuItem>
            {clinicalLocations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="Discharge from"
          InputLabelProps={{ shrink: true }}
          value={filters.dateFrom || ""}
          onChange={(e) => updateFilter("dateFrom", e.target.value || null)}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="Discharge to"
          InputLabelProps={{ shrink: true }}
          value={filters.dateTo || ""}
          onChange={(e) => updateFilter("dateTo", e.target.value || null)}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          type="number"
          label="Min LACE+"
          value={filters.minLace ?? ""}
          onChange={(e) =>
            updateFilter("minLace", e.target.value === "" ? null : Number(e.target.value))
          }
          sx={{ minWidth: 120 }}
          inputProps={{ min: 0 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="discharged-status-label">Follow-up status</InputLabel>
          <Select
            labelId="discharged-status-label"
            value={filters.followUpStatusId ?? ""}
            label="Follow-up status"
            onChange={(e) =>
              updateFilter("followUpStatusId", e.target.value === "" ? null : e.target.value)
            }
          >
            <MenuItem value="">All</MenuItem>
            {FOLLOW_UP_STATUS_OPTIONS.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={!!filters.onlyOverdue}
              onChange={(e) => updateFilter("onlyOverdue", e.target.checked || null)}
            />
          }
          label="Only overdue"
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
        >
          Reset
        </Button>
      </Box>

      {/* Tabla */}
      <div ref={tableRef} style={{ height: 560, width: "100%" }}>
        <DataGrid
          getRowId={(row) => row.dischargedEventId}
          rows={dischargedPatients}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
          getRowClassName={(params) => (params.row.isOverdue ? "row-overdue" : "")}
          sx={{
            "& .row-overdue": { backgroundColor: "#FDECEA" },
            "& .row-overdue:hover": { backgroundColor: "#FADBD8" },
          }}
        />
      </div>
    </>
  );
};

export default DischargedTable;
